// src/entriesApi.js
// This file maintains backward compatibility with the old user-scoped model
// For album-scoped operations, use albumApi.js instead

import { db, auth } from "./firebase";
import { collection, query, where, getDocs, getDoc } from "firebase/firestore";
import { fetchPhotosByIds } from "./utils/googlePhotosApi";
import { getFunctions, httpsCallable } from "firebase/functions";
import { updateDoc, doc } from "firebase/firestore";
import { isCustomPlaceId } from "./utils/customPlacesApi";
import { isCallableRateLimitError, rateLimitRetryDelayMs, sleep } from "./utils/functionRetryUtils";
import { getDriveDisplayUrl } from "./utils/driveUrls";

// Import album-scoped functions
import * as albumApi from './albumApi';

const functions = getFunctions(); // Initialize Firebase Functions
const getPhotoCaption = httpsCallable(functions, "getPhotoCaption");
const getPlaceDetails = httpsCallable(functions, "getPlaceDetails");

// Wrapper function to handle album-scoped or user-scoped fetching
export const fetchEntryByDate = async (date, albumId = null) => {
  // If albumId is provided, use album-scoped API
  if (albumId) {
    return await albumApi.fetchEntryByDate(albumId, date);
  }
  
  // Otherwise, use old user-scoped API
  return await fetchEntryByDateLegacy(date);
};

// Rate-limit wrapper for getPhotoCaption
async function safeGetPhotoCaption(params, attempt = 0) {
  try {
    return await getPhotoCaption(params);
  } catch (error) {
    if (isCallableRateLimitError(error) && attempt < 4) {
      const { limit, remaining, reset } = error.details || {};
      const waitMs = rateLimitRetryDelayMs(reset, attempt);
      console.warn(`OpenAI rate limited: ${remaining}/${limit} remaining, retrying in ${waitMs}ms`);
      await sleep(waitMs);
      return safeGetPhotoCaption(params, attempt + 1);
    }
    throw error;
  }
}

export const fetchEntries = async () => {
  const user = auth.currentUser;
  if (user) {
    const q = query(collection(db, `users/${user.uid}/entries`), where("userId", "==", user.uid));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  }
  return [];
};

// Legacy function - kept for backward compatibility
const fetchEntryByDateLegacy = async (date) => {
  const user = auth.currentUser;
  if (user) {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const q = query(
      collection(db, `users/${user.uid}/entries`),
      where("userId", "==", user.uid),
      where("createdAt", ">=", startOfDay),
      where("createdAt", "<=", endOfDay)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      let entry = { id: doc.id, ...doc.data() };

      // Load photo details
      if (entry.photos && entry.photos.length > 0) { // entry.photos are from Firestore
        const firestorePhotos = entry.photos; 
        const googlePhotoIdsToFetch = firestorePhotos
          .filter(p => !p.driveFileId && p.id) 
          .map(p => p.id);

        let googlePhotosApiResults = [];
        if (googlePhotoIdsToFetch.length > 0) {
          googlePhotosApiResults = await fetchPhotosByIds(googlePhotoIdsToFetch);
          // Handle case where fetchPhotosByIds returns null due to API failure
          if (!googlePhotosApiResults) {
            googlePhotosApiResults = [];
          }
        }

        entry.photos = firestorePhotos.map(firestorePhoto => {
          if (firestorePhoto.driveFileId) {
            // This is a Google Drive file
            return {
              ...firestorePhoto, // Includes id (which is the Firestore doc ID for this photo entry), driveFileId, filename, mimeType, date_taken, place, caption, etc. from Firestore
              baseUrl: getDriveDisplayUrl(firestorePhoto.driveFileId),
              // mediaMetadata might be absent or minimal for Drive files unless stored in Firestore
            };
          } else {
            // This is a Google Photos file
            const apiPhotoData = googlePhotosApiResults.find(apiP => apiP.id === firestorePhoto.id); // firestorePhoto.id is the GPhotos media ID
            if (apiPhotoData) {
              return {
                ...apiPhotoData, // Base data from Google Photos API (includes baseUrl, mediaMetadata, mimeType if API provided it)
                id: firestorePhoto.id, // Ensure Firestore ID (which is GPhotos media ID here) is used
                driveFileId: firestorePhoto.driveFileId, // Should be undefined here
                date_taken: firestorePhoto.date_taken || apiPhotoData.date_taken || (apiPhotoData.mediaMetadata && apiPhotoData.mediaMetadata.creationTime),
                filename: firestorePhoto.filename || apiPhotoData.filename,
                latitude: firestorePhoto.latitude,
                longitude: firestorePhoto.longitude,
                place: firestorePhoto.place,
                caption: firestorePhoto.caption,
                story_caption: firestorePhoto.story_caption,
                story_caption_score: firestorePhoto.story_caption_score,
                mimeType: firestorePhoto.mimeType || apiPhotoData.mimeType,
              };
            }
            console.warn(`Google Photos API data not found for ID: ${firestorePhoto.id}. Returning Firestore data.`);
            return { ...firestorePhoto }; // Fallback, might lack baseUrl
          }
        });
      }

      return entry;
    }
  }
  return null;
};

// Wrapper for fetchMultipleEntriesByIds
export const fetchMultipleEntriesByIds = async (entryIds, setFetchProgress, albumId = null) => {
  // If albumId is provided, use album-scoped API
  if (albumId) {
    return await albumApi.fetchMultipleEntriesByIds(albumId, entryIds, setFetchProgress);
  }
  
  // Otherwise, use old user-scoped API
  return await fetchMultipleEntriesByIdsLegacy(entryIds, setFetchProgress);
};

// Legacy function - kept for backward compatibility
const fetchMultipleEntriesByIdsLegacy = async (entryIds, setFetchProgress) => {
  const user = auth.currentUser;
  const entries = [];
  const totalEntries = entryIds.length;
  
  if (user && entryIds.length > 0) {
    let captionsAdded = false;
    let storyCaptionsAdded = false;
    
    for (let i = 0; i < entryIds.length; i++) {
      const entryId = entryIds[i];
      
      try {
        // Get the entry document directly by ID
        const entryDoc = await getDoc(doc(db, `users/${user.uid}/entries`, entryId));
        
        if (entryDoc.exists()) {
          let entry = { id: entryDoc.id, ...entryDoc.data() };
          
          // Load photo details - this is similar to fetchEntryByDate logic
          if (entry.photos && entry.photos.length > 0) {
            const firestorePhotos = entry.photos;
            const firestorePhotosToProcess = [...firestorePhotos]; // Make a copy for updating captions etc.
            
            // Separate Google Photos from Drive files
            const googlePhotoIdsToFetch = firestorePhotos
              .filter(p => !p.driveFileId && p.id)
              .map(p => p.id);
            
            let googlePhotosApiResults = [];
            if (googlePhotoIdsToFetch.length > 0) {
              googlePhotosApiResults = await fetchPhotosByIds(googlePhotoIdsToFetch);
              // Handle case where fetchPhotosByIds returns null due to API failure
              if (!googlePhotosApiResults) {
                googlePhotosApiResults = [];
              }
            }
            
            // Process each photo
            entry.photos = await Promise.all(firestorePhotos.map(async (firestorePhotoData) => {
              let mergedPhoto;
              
              if (firestorePhotoData.driveFileId) {
                // This is a Google Drive file
                mergedPhoto = {
                  ...firestorePhotoData,
                  baseUrl: getDriveDisplayUrl(firestorePhotoData.driveFileId),
                };
              } else {
                // This is a Google Photos file
                const apiPhotoData = googlePhotosApiResults.find(p => p.id === firestorePhotoData.id);
                
                if (apiPhotoData) {
                  mergedPhoto = {
                    ...firestorePhotoData,
                    baseUrl: apiPhotoData.baseUrl,
                    productUrl: apiPhotoData.productUrl,
                    mediaMetadata: apiPhotoData.mediaMetadata,
                  };
                } else {
                  console.warn(`Google Photos API data not found for ID: ${firestorePhotoData.id} in fetchMultipleEntriesByIds. Using Firestore data.`);
                  mergedPhoto = { ...firestorePhotoData }; // Fallback to just using Firestore data
                }
              }
              
              // Handle place details if needed
              if (
                mergedPhoto.place &&
                !mergedPhoto.place.address &&
                mergedPhoto.place.place_id &&
                !isCustomPlaceId(mergedPhoto.place.place_id)
              ) {
                try {
                  const result = await getPlaceDetails({ placeId: mergedPhoto.place.place_id });
                  const address = result.data.address;
                  // Update the firestorePhotoData directly as it's used for saving back
                  const originalFirestorePhoto = firestorePhotosToProcess.find(p => p.id === mergedPhoto.id);
                  if (originalFirestorePhoto && originalFirestorePhoto.place) {
                    originalFirestorePhoto.place.address = address;
                  }
                  mergedPhoto.place.address = address;
                } catch (error) {
                  console.error("Error fetching place details:", error);
                }
              }
              
              // Handle captions if needed
              let photoCaptionsAdded = false;
              if (mergedPhoto.baseUrl && (!mergedPhoto.caption || !mergedPhoto.story_caption || mergedPhoto.story_caption_score === undefined)) {
                try {
                  const result = await safeGetPhotoCaption({
                    baseUrl: mergedPhoto.baseUrl,
                    entryContent: entry.content,
                    dateTaken: mergedPhoto.date_taken,
                    place: mergedPhoto.place || null,
                    targetWidth: 800,
                  });
                  const { caption, story_caption, score } = result.data;
                  
                  // Update the firestorePhotoData for saving
                  const originalFirestorePhoto = firestorePhotosToProcess.find(p => p.id === mergedPhoto.id);
                  if (originalFirestorePhoto) {
                    originalFirestorePhoto.caption = caption;
                    originalFirestorePhoto.story_caption = story_caption;
                    originalFirestorePhoto.story_caption_score = score;
                  }
                  
                  mergedPhoto.caption = caption;
                  mergedPhoto.story_caption = story_caption;
                  mergedPhoto.story_caption_score = score;
                  photoCaptionsAdded = true;
                } catch (error) {
                  console.error("Error generating photo analysis:", error);
                }
              }
              
              return { mergedPhoto, photoCaptionsAdded };
            }));
            
            // Check if any captions were added and update the flag
            for (const result of entry.photos) {
              if (result.photoCaptionsAdded) {
                captionsAdded = true;
              }
            }
            
            // Extract just the merged photos for the entry
            entry.photos = entry.photos.map(result => result.mergedPhoto);
            
            // Update Firestore if any captions or place details were added
            if (captionsAdded || storyCaptionsAdded || firestorePhotosToProcess.some(p => p.place && p.place.address)) {
              const entryRef = doc(db, `users/${user.uid}/entries`, entry.id);
              await updateDoc(entryRef, {
                photos: firestorePhotosToProcess,
              });
            }
          }
          
          entries.push(entry);
        } else {
          console.warn(`Entry with ID ${entryId} not found`);
        }
      } catch (error) {
        console.error(`Error fetching entry with ID ${entryId}:`, error);
      }
      
      // Update fetch progress
      if (setFetchProgress) {
        const currentProgress = Math.round(((i + 1) / totalEntries) * 100);
        setFetchProgress(currentProgress);
      }
    }
  }
  
  return entries;
};

// Wrapper for fetchEntriesByDates
export const fetchEntriesByDates = async (dates, chatGptToken, setFetchProgress, albumId = null) => {
  // If albumId is provided, use album-scoped API
  if (albumId) {
    return await albumApi.fetchEntriesByDates(albumId, dates, setFetchProgress);
  }
  
  // Otherwise, use old user-scoped API
  return await fetchEntriesByDatesLegacy(dates, chatGptToken, setFetchProgress);
};

// Legacy function - kept for backward compatibility
const fetchEntriesByDatesLegacy = async (dates, chatGptToken, setFetchProgress) => {
  const user = auth.currentUser;
  const entries = [];
  const totalDates = dates.length;
  
  if (user) {
    for (let i = 0; i < totalDates; i++) {
      const date = dates[i];
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, `users/${user.uid}/entries`),
        where("userId", "==", user.uid),
        where("createdAt", ">=", startOfDay),
        where("createdAt", "<=", endOfDay)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        let entry = { id: docSnap.id, ...docSnap.data() };

        // Initialize flags
        let captionsAdded = false;
        let storyCaptionsAdded = false;

        const firestorePhotosToProcess = entry.photos ? [...entry.photos] : []; // Original photos from Firestore for this entry

        if (firestorePhotosToProcess.length > 0) {
          const googlePhotoIdsToFetch = firestorePhotosToProcess
            .filter(p => !p.driveFileId && p.id) // Filter out Drive files and ensure p.id exists
            .map(p => p.id);

          let googlePhotosApiResults = [];
          if (googlePhotoIdsToFetch.length > 0) {
            googlePhotosApiResults = await fetchPhotosByIds(googlePhotoIdsToFetch);
          }
          
          // This mapping will now create the final `entry.photos` array
          entry.photos = await Promise.all(firestorePhotosToProcess.map(async (firestorePhotoData) => {
            let mergedPhoto;

            if (firestorePhotoData.driveFileId) {
              // Google Drive File
              mergedPhoto = {
                ...firestorePhotoData, // Includes 'id' from Firestore
                baseUrl: getDriveDisplayUrl(firestorePhotoData.driveFileId),
              };
            } else {
              // Google Photos File
              const apiPhotoData = googlePhotosApiResults.find(p => p.id === firestorePhotoData.id); // firestorePhotoData.id is GPhotos Media ID
              if (apiPhotoData) {
                mergedPhoto = {
                  ...apiPhotoData, // Base from Google Photos API
                  id: firestorePhotoData.id, // Firestore ID (which is GPhotos media ID here)
                  driveFileId: firestorePhotoData.driveFileId, // undefined
                  date_taken: firestorePhotoData.date_taken || apiPhotoData.date_taken || (apiPhotoData.mediaMetadata && apiPhotoData.mediaMetadata.creationTime),
                  filename: firestorePhotoData.filename || apiPhotoData.filename,
                  latitude: firestorePhotoData.latitude,
                  longitude: firestorePhotoData.longitude,
                  place: firestorePhotoData.place,
                  caption: firestorePhotoData.caption,
                  story_caption: firestorePhotoData.story_caption,
                  story_caption_score: firestorePhotoData.story_caption_score,
                  mimeType: firestorePhotoData.mimeType || apiPhotoData.mimeType,
                };
              } else {
                console.warn(`Google Photos API data not found for ID: ${firestorePhotoData.id} in fetchEntriesByDates. Using Firestore data.`);
                mergedPhoto = { ...firestorePhotoData }; // Fallback, might lack baseUrl or full metadata
              }
            }

            // Common post-merge processing (place details, captions)
            if (
              mergedPhoto.place &&
              !mergedPhoto.place.address &&
              mergedPhoto.place.place_id &&
              !isCustomPlaceId(mergedPhoto.place.place_id)
            ) {
              try {
                const result = await getPlaceDetails({ placeId: mergedPhoto.place.place_id });
                const address = result.data.address;
                // Update the firestorePhotoData directly as it's used for saving back
                const originalFirestorePhoto = firestorePhotosToProcess.find(p => p.id === mergedPhoto.id);
                if (originalFirestorePhoto && originalFirestorePhoto.place) {
                    originalFirestorePhoto.place.address = address;
                  }
                mergedPhoto.place.address = address;
              } catch (error) {
                console.error("Error fetching place details:", error);
              }
            }

            if (mergedPhoto.baseUrl && (!mergedPhoto.caption || !mergedPhoto.story_caption || mergedPhoto.story_caption_score === undefined)) {
              try {
                const result = await safeGetPhotoCaption({
                  baseUrl: mergedPhoto.baseUrl, // This needs to work for Drive URLs too if AI captioning is desired for them
                  entryContent: entry.content,
                  dateTaken: mergedPhoto.date_taken,
                  place: mergedPhoto.place || null,
                  targetWidth: 800,
                });
                const { caption, story_caption, score } = result.data;
                // Update the firestorePhotoData for saving
                const originalFirestorePhoto = firestorePhotosToProcess.find(p => p.id === mergedPhoto.id);
                if (originalFirestorePhoto) {
                    originalFirestorePhoto.caption = caption;
                    originalFirestorePhoto.story_caption = story_caption;
                    originalFirestorePhoto.story_caption_score = score;
                }
                mergedPhoto.caption = caption;
                mergedPhoto.story_caption = story_caption;
                mergedPhoto.story_caption_score = score;
                captionsAdded = true; 
              } catch (error) {
                console.error("Error generating photo analysis:", error);
              }
            }
            return mergedPhoto;
          }));
        }

        // Update Firestore if any captions, story captions, or addresses exist
        // The `firestorePhotosToProcess` array (which is a copy of `entry.photos` from Firestore)
        // has been modified in place by the caption/place logic.
        if (captionsAdded || storyCaptionsAdded || firestorePhotosToProcess.some(p => p.place && p.place.address)) {
          const entryRef = doc(db, `users/${user.uid}/entries`, entry.id);
          await updateDoc(entryRef, {
            photos: firestorePhotosToProcess, // Save the modified Firestore photo objects
          });
        }

        entries.push(entry); // Push the entry with fully resolved photo details (baseUrls etc.)
      }
      
      // --- Update Fetch Progress ---
      const currentProgress = Math.round(((i + 1) / totalDates) * 100);
      if (setFetchProgress) { // Check if the function was passed
          setFetchProgress(currentProgress);
      }
      // --- End Update Fetch Progress ---
    }
    return entries;
  }
  return [];
};

export { getDriveDisplayUrl };
